/**
 * UranIA PA — Onboarding R&I stepper UI (US-016).
 *
 * Route `/onboarding/ri` per il Beneficiario R&I (analisi §10 BC
 * Onboarding + CLAUDE.md "Onboarding flows differ by role" — campi
 * distinti rispetto al PA, persistiti in `user_profiles_ri`). Implementa
 * i 5 step della wizard R&I:
 *   1. Anagrafica + ente (nome, cognome, codice_fiscale, denominazione_ente,
 *      telefono opz., codice_ipa_ente opz.)
 *   2. Settore di ricerca (italian-first)
 *   3. Dominio S3 (Strategia di Specializzazione Intelligente regionale)
 *   4. Tipo ente (universita / epr / pmi / grande_impresa / odr)
 *   5. TRL range + profilo tipo (ricercatore / pm / cto)
 *
 * Acceptance Criteria coperti:
 *   - AC#1 — file presente con stepper Bootstrap Italia (riusa
 *     `OnboardingStepper` US-015).
 *   - AC#2 — 5 form allineati 1:1 a OAS `/api/v1/onboarding/ri/...`.
 *   - AC#3 — reload-safe via `ri-session-storage` (sessionStorage
 *     chiavato per subject); source-of-truth resta il backend
 *     (`current_step` ritornato dallo step response).
 *   - AC#4 — axe-core 0 serious (verificato da
 *     `spa-onboarding-ri-axe.spec.ts`).
 *   - AC#5 — typecheck (tsc -b strict) verde.
 *   - AC#6 — dev-browser fallback Playwright chromium (CLAUDE.md §Step 3).
 *
 * Pre-condizioni di accesso:
 *   - utente autenticato Keycloak (auth-store popolato);
 *   - claim realm role `beneficiario_ri` (server-side enforcement —
 *     US-014); lato SPA mostriamo CTA "Vai al login" se manca sessione.
 *
 * NB step-up MFA RFC 9470 (acr ∈ {L2, L3, CIE, fido2}) e' verificato
 * server-side (US-014): in caso di 403 con `WWW-Authenticate: step-up`
 * il client SPA dovra' riavviare login con `acr_values` opportuno
 * (US-022+ implementera' l'auto-redirect; in Fase 1 mostriamo errore
 * inline italian-first).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import type { FieldValues, Resolver, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { JSX, ReactElement } from 'react';
import type { ZodType } from 'zod';

import { selectIsAuthenticated, selectPrincipal, useAuthStore } from '../../lib/auth/auth-store.ts';
import {
  createOnboardingRiSession,
  OnboardingApiError,
  submitOnboardingRiStep,
  type OnboardingRiStepBody,
} from '../../lib/onboarding/ri-api.ts';
import {
  ONBOARDING_RI_TOTAL_STEPS,
  PROFILO_TIPO_VALUES,
  TIPO_ENTE_VALUES,
  onboardingRiStep1Schema,
  onboardingRiStep2Schema,
  onboardingRiStep3Schema,
  onboardingRiStep4Schema,
  onboardingRiStep5Schema,
  type OnboardingRiSessionDto,
  type OnboardingRiStep1Body,
  type OnboardingRiStep2Body,
  type OnboardingRiStep3Body,
  type OnboardingRiStep4Body,
  type OnboardingRiStep5Body,
  type OnboardingRiStepNumber,
  type OnboardingRiStepResponseDto,
  type ProfiloTipoRi,
  type TipoEnteRi,
} from '../../lib/onboarding/ri-schemas.ts';
import {
  clearOnboardingRiSnapshot,
  loadOnboardingRiSnapshot,
  saveOnboardingRiSnapshot,
  snapshotFromSessionDto,
} from '../../lib/onboarding/ri-session-storage.ts';
import {
  OnboardingStepper,
  type OnboardingStepperStep,
} from '../../components/onboarding/OnboardingStepper.tsx';
import { Route as RootRoute } from '../__root.tsx';

/**
 * Costruisce un `Resolver<T>` da uno schema Zod v4 in modo type-erasure-safe
 * (vedi `pa.tsx` per la stessa motivazione applicata a Zod 4 +
 * @hookform/resolvers 5 + `exactOptionalPropertyTypes`).
 */
function buildResolver<T extends FieldValues>(schema: ZodType): Resolver<T> {
  return zodResolver(schema as never) as Resolver<T>;
}

interface StepFormBaseProps<TBody extends FieldValues> {
  readonly defaultValues: Partial<TBody>;
  readonly onSubmitStep: (body: TBody) => Promise<void>;
  readonly onBack: (() => void) | null;
  readonly submitting: boolean;
}

function StepCard(props: { readonly title: string; readonly children: ReactElement }): JSX.Element {
  return (
    <section className="card shadow-sm" aria-labelledby="onboarding-ri-step-heading">
      <div className="card-body">
        <h2 id="onboarding-ri-step-heading" className="h4 mb-3">
          {props.title}
        </h2>
        {props.children}
      </div>
    </section>
  );
}

function StepNav(props: {
  readonly onBack: (() => void) | null;
  readonly submitting: boolean;
  readonly nextLabel: string;
  readonly backLabel: string;
}): JSX.Element {
  return (
    <div className="d-flex justify-content-between mt-4 gap-2">
      <button
        type="button"
        className="btn btn-outline-primary"
        onClick={props.onBack ?? undefined}
        disabled={props.onBack === null || props.submitting}
        data-testid="onboarding-ri-back"
      >
        {props.backLabel}
      </button>
      <button
        type="submit"
        className="btn btn-primary"
        disabled={props.submitting}
        data-testid="onboarding-ri-next"
      >
        {props.submitting ? `${props.nextLabel}…` : props.nextLabel}
      </button>
    </div>
  );
}

function Step1AnagraficaEnte(props: StepFormBaseProps<OnboardingRiStep1Body>): JSX.Element {
  const { t } = useTranslation();
  const { defaultValues, onSubmitStep, onBack, submitting } = props;
  const resolver = buildResolver<OnboardingRiStep1Body>(onboardingRiStep1Schema);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingRiStep1Body>({
    resolver,
    defaultValues,
  });

  const submit: SubmitHandler<OnboardingRiStep1Body> = async (data) => {
    const trimmedTelefono = typeof data.telefono === 'string' ? data.telefono.trim() : '';
    const trimmedIpa = typeof data.codice_ipa_ente === 'string' ? data.codice_ipa_ente.trim() : '';
    const normalized: OnboardingRiStep1Body = {
      nome: data.nome,
      cognome: data.cognome,
      codice_fiscale: data.codice_fiscale.toUpperCase(),
      denominazione_ente: data.denominazione_ente,
      ...(trimmedTelefono.length > 0 ? { telefono: trimmedTelefono } : {}),
      ...(trimmedIpa.length > 0 ? { codice_ipa_ente: trimmedIpa } : {}),
    };
    await onSubmitStep(normalized);
  };

  return (
    <StepCard title={t('onboarding.ri.steps.s1.title')}>
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(submit)(event);
        }}
        data-testid="onboarding-ri-form-step-1"
      >
        <div className="form-group">
          <label htmlFor="onboarding-ri-nome" className="form-label">
            {t('onboarding.ri.fields.nome')}
          </label>
          <input
            id="onboarding-ri-nome"
            type="text"
            className="form-control"
            autoComplete="given-name"
            aria-invalid={errors.nome !== undefined}
            aria-describedby={errors.nome !== undefined ? 'onboarding-ri-nome-error' : undefined}
            {...register('nome')}
          />
          {errors.nome !== undefined && (
            <small id="onboarding-ri-nome-error" className="form-text text-danger">
              {t('onboarding.ri.errors.field_required')}
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="onboarding-ri-cognome" className="form-label">
            {t('onboarding.ri.fields.cognome')}
          </label>
          <input
            id="onboarding-ri-cognome"
            type="text"
            className="form-control"
            autoComplete="family-name"
            aria-invalid={errors.cognome !== undefined}
            aria-describedby={
              errors.cognome !== undefined ? 'onboarding-ri-cognome-error' : undefined
            }
            {...register('cognome')}
          />
          {errors.cognome !== undefined && (
            <small id="onboarding-ri-cognome-error" className="form-text text-danger">
              {t('onboarding.ri.errors.field_required')}
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="onboarding-ri-cf" className="form-label">
            {t('onboarding.ri.fields.codice_fiscale')}
          </label>
          <input
            id="onboarding-ri-cf"
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={16}
            className="form-control"
            aria-invalid={errors.codice_fiscale !== undefined}
            aria-describedby={
              errors.codice_fiscale !== undefined
                ? 'onboarding-ri-cf-error'
                : 'onboarding-ri-cf-hint'
            }
            {...register('codice_fiscale')}
          />
          <small id="onboarding-ri-cf-hint" className="form-text text-muted">
            {t('onboarding.ri.fields.codice_fiscale_hint')}
          </small>
          {errors.codice_fiscale !== undefined && (
            <small id="onboarding-ri-cf-error" className="form-text text-danger">
              {t('onboarding.ri.errors.codice_fiscale_invalid')}
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="onboarding-ri-ente" className="form-label">
            {t('onboarding.ri.fields.denominazione_ente')}
          </label>
          <input
            id="onboarding-ri-ente"
            type="text"
            className="form-control"
            aria-invalid={errors.denominazione_ente !== undefined}
            aria-describedby={
              errors.denominazione_ente !== undefined
                ? 'onboarding-ri-ente-error'
                : 'onboarding-ri-ente-hint'
            }
            {...register('denominazione_ente')}
          />
          <small id="onboarding-ri-ente-hint" className="form-text text-muted">
            {t('onboarding.ri.fields.denominazione_ente_hint')}
          </small>
          {errors.denominazione_ente !== undefined && (
            <small id="onboarding-ri-ente-error" className="form-text text-danger">
              {t('onboarding.ri.errors.field_required')}
            </small>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="onboarding-ri-tel" className="form-label">
            {t('onboarding.ri.fields.telefono_optional')}
          </label>
          <input
            id="onboarding-ri-tel"
            type="tel"
            className="form-control"
            autoComplete="tel"
            {...register('telefono')}
          />
        </div>
        <div className="form-group">
          <label htmlFor="onboarding-ri-ipa" className="form-label">
            {t('onboarding.ri.fields.codice_ipa_ente_optional')}
          </label>
          <input
            id="onboarding-ri-ipa"
            type="text"
            className="form-control"
            aria-describedby="onboarding-ri-ipa-hint"
            {...register('codice_ipa_ente')}
          />
          <small id="onboarding-ri-ipa-hint" className="form-text text-muted">
            {t('onboarding.ri.fields.codice_ipa_ente_hint')}
          </small>
        </div>
        <StepNav
          onBack={onBack}
          submitting={submitting}
          nextLabel={t('onboarding.ri.actions.next')}
          backLabel={t('onboarding.ri.actions.back')}
        />
      </form>
    </StepCard>
  );
}

function Step2Settore(props: StepFormBaseProps<OnboardingRiStep2Body>): JSX.Element {
  const { t } = useTranslation();
  const { defaultValues, onSubmitStep, onBack, submitting } = props;
  const resolver = buildResolver<OnboardingRiStep2Body>(onboardingRiStep2Schema);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingRiStep2Body>({ resolver, defaultValues });

  return (
    <StepCard title={t('onboarding.ri.steps.s2.title')}>
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmitStep)(event);
        }}
        data-testid="onboarding-ri-form-step-2"
      >
        <div className="form-group">
          <label htmlFor="onboarding-ri-settore" className="form-label">
            {t('onboarding.ri.fields.settore_ricerca')}
          </label>
          <input
            id="onboarding-ri-settore"
            type="text"
            className="form-control"
            aria-invalid={errors.settore_ricerca !== undefined}
            aria-describedby={
              errors.settore_ricerca !== undefined
                ? 'onboarding-ri-settore-error'
                : 'onboarding-ri-settore-hint'
            }
            {...register('settore_ricerca')}
          />
          <small id="onboarding-ri-settore-hint" className="form-text text-muted">
            {t('onboarding.ri.fields.settore_ricerca_hint')}
          </small>
          {errors.settore_ricerca !== undefined && (
            <small id="onboarding-ri-settore-error" className="form-text text-danger">
              {t('onboarding.ri.errors.field_required')}
            </small>
          )}
        </div>
        <StepNav
          onBack={onBack}
          submitting={submitting}
          nextLabel={t('onboarding.ri.actions.next')}
          backLabel={t('onboarding.ri.actions.back')}
        />
      </form>
    </StepCard>
  );
}

function Step3DominioS3(props: StepFormBaseProps<OnboardingRiStep3Body>): JSX.Element {
  const { t } = useTranslation();
  const { defaultValues, onSubmitStep, onBack, submitting } = props;
  const resolver = buildResolver<OnboardingRiStep3Body>(onboardingRiStep3Schema);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingRiStep3Body>({ resolver, defaultValues });

  return (
    <StepCard title={t('onboarding.ri.steps.s3.title')}>
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmitStep)(event);
        }}
        data-testid="onboarding-ri-form-step-3"
      >
        <div className="form-group">
          <label htmlFor="onboarding-ri-s3" className="form-label">
            {t('onboarding.ri.fields.dominio_s3')}
          </label>
          <input
            id="onboarding-ri-s3"
            type="text"
            className="form-control"
            aria-invalid={errors.dominio_s3 !== undefined}
            aria-describedby={
              errors.dominio_s3 !== undefined ? 'onboarding-ri-s3-error' : 'onboarding-ri-s3-hint'
            }
            {...register('dominio_s3')}
          />
          <small id="onboarding-ri-s3-hint" className="form-text text-muted">
            {t('onboarding.ri.fields.dominio_s3_hint')}
          </small>
          {errors.dominio_s3 !== undefined && (
            <small id="onboarding-ri-s3-error" className="form-text text-danger">
              {t('onboarding.ri.errors.field_required')}
            </small>
          )}
        </div>
        <StepNav
          onBack={onBack}
          submitting={submitting}
          nextLabel={t('onboarding.ri.actions.next')}
          backLabel={t('onboarding.ri.actions.back')}
        />
      </form>
    </StepCard>
  );
}

function Step4TipoEnte(props: StepFormBaseProps<OnboardingRiStep4Body>): JSX.Element {
  const { t } = useTranslation();
  const { defaultValues, onSubmitStep, onBack, submitting } = props;
  const resolver = buildResolver<OnboardingRiStep4Body>(onboardingRiStep4Schema);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingRiStep4Body>({ resolver, defaultValues });

  return (
    <StepCard title={t('onboarding.ri.steps.s4.title')}>
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmitStep)(event);
        }}
        data-testid="onboarding-ri-form-step-4"
      >
        <fieldset className="form-group">
          <legend className="form-label">{t('onboarding.ri.fields.tipo_ente')}</legend>
          {TIPO_ENTE_VALUES.map((value: TipoEnteRi) => {
            const id = `onboarding-ri-tipo-ente-${value}`;
            return (
              <div key={value} className="form-check">
                <input
                  id={id}
                  type="radio"
                  className="form-check-input"
                  value={value}
                  {...register('tipo_ente')}
                />
                <label htmlFor={id} className="form-check-label">
                  {t(`onboarding.ri.fields.tipo_ente_options.${value}`)}
                </label>
              </div>
            );
          })}
          {errors.tipo_ente !== undefined && (
            <small className="form-text text-danger">
              {t('onboarding.ri.errors.field_required')}
            </small>
          )}
        </fieldset>
        <StepNav
          onBack={onBack}
          submitting={submitting}
          nextLabel={t('onboarding.ri.actions.next')}
          backLabel={t('onboarding.ri.actions.back')}
        />
      </form>
    </StepCard>
  );
}

function Step5TrlProfilo(props: StepFormBaseProps<OnboardingRiStep5Body>): JSX.Element {
  const { t } = useTranslation();
  const { defaultValues, onSubmitStep, onBack, submitting } = props;
  const resolver = buildResolver<OnboardingRiStep5Body>(onboardingRiStep5Schema);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OnboardingRiStep5Body>({ resolver, defaultValues });

  return (
    <StepCard title={t('onboarding.ri.steps.s5.title')}>
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmitStep)(event);
        }}
        data-testid="onboarding-ri-form-step-5"
      >
        <p className="mb-3 small text-muted">{t('onboarding.ri.steps.s5.intro')}</p>
        <div className="form-group">
          <label htmlFor="onboarding-ri-trl" className="form-label">
            {t('onboarding.ri.fields.trl_range')}
          </label>
          <input
            id="onboarding-ri-trl"
            type="text"
            className="form-control"
            aria-invalid={errors.trl_range !== undefined}
            aria-describedby={
              errors.trl_range !== undefined ? 'onboarding-ri-trl-error' : 'onboarding-ri-trl-hint'
            }
            {...register('trl_range')}
          />
          <small id="onboarding-ri-trl-hint" className="form-text text-muted">
            {t('onboarding.ri.fields.trl_range_hint')}
          </small>
          {errors.trl_range !== undefined && (
            <small id="onboarding-ri-trl-error" className="form-text text-danger">
              {t('onboarding.ri.errors.field_required')}
            </small>
          )}
        </div>
        <fieldset className="form-group">
          <legend className="form-label">{t('onboarding.ri.fields.profilo_tipo')}</legend>
          {PROFILO_TIPO_VALUES.map((value: ProfiloTipoRi) => {
            const id = `onboarding-ri-profilo-${value}`;
            return (
              <div key={value} className="form-check">
                <input
                  id={id}
                  type="radio"
                  className="form-check-input"
                  value={value}
                  {...register('profilo_tipo')}
                />
                <label htmlFor={id} className="form-check-label">
                  {t(`onboarding.ri.fields.profilo_tipo_options.${value}`)}
                </label>
              </div>
            );
          })}
          {errors.profilo_tipo !== undefined && (
            <small className="form-text text-danger">
              {t('onboarding.ri.errors.field_required')}
            </small>
          )}
        </fieldset>
        <StepNav
          onBack={onBack}
          submitting={submitting}
          nextLabel={t('onboarding.ri.actions.finalize')}
          backLabel={t('onboarding.ri.actions.back')}
        />
      </form>
    </StepCard>
  );
}

interface OnboardingRiLoadingState {
  readonly kind: 'loading';
}
interface OnboardingRiErrorState {
  readonly kind: 'error';
  readonly title: string;
  readonly detail: string | null;
}
interface OnboardingRiReadyState {
  readonly kind: 'ready';
  readonly session: OnboardingRiSessionDto;
  readonly activeStep: OnboardingRiStepNumber;
}
interface OnboardingRiDoneState {
  readonly kind: 'done';
  readonly session: OnboardingRiStepResponseDto;
}

type OnboardingRiState =
  | OnboardingRiLoadingState
  | OnboardingRiErrorState
  | OnboardingRiReadyState
  | OnboardingRiDoneState;

function nextStep(current: number): OnboardingRiStepNumber {
  const candidate = Math.min(current + 1, ONBOARDING_RI_TOTAL_STEPS);
  return Math.max(1, candidate) as OnboardingRiStepNumber;
}

function clampActiveStep(currentStep: number, requested: number): OnboardingRiStepNumber {
  const maxAllowed = Math.min(ONBOARDING_RI_TOTAL_STEPS, currentStep + 1);
  const safe = Math.max(1, Math.min(requested, maxAllowed));
  return safe as OnboardingRiStepNumber;
}

export function OnboardingRiPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const subject = principal?.subject ?? '';

  const [state, setState] = useState<OnboardingRiState>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const stepperSteps = useMemo<readonly OnboardingStepperStep[]>(
    () => [
      { number: 1, label: t('onboarding.ri.steps.s1.label') },
      { number: 2, label: t('onboarding.ri.steps.s2.label') },
      { number: 3, label: t('onboarding.ri.steps.s3.label') },
      { number: 4, label: t('onboarding.ri.steps.s4.label') },
      { number: 5, label: t('onboarding.ri.steps.s5.label') },
    ],
    [t],
  );

  useEffect(() => {
    if (!isAuthenticated || subject.length === 0) {
      return;
    }
    const cancellation = { cancelled: false };
    void (async (): Promise<void> => {
      try {
        const snapshot = loadOnboardingRiSnapshot(subject);
        const { session } = await createOnboardingRiSession();
        if (cancellation.cancelled) {
          return;
        }
        saveOnboardingRiSnapshot(snapshotFromSessionDto(subject, session));
        const completed =
          typeof session.completed_at === 'string' && session.completed_at.length > 0;
        if (completed) {
          setState({ kind: 'done', session });
          return;
        }
        const resumed =
          snapshot !== null && snapshot.sessionId === session.session_id
            ? clampActiveStep(session.current_step, snapshot.currentStep + 1)
            : nextStep(session.current_step);
        setState({ kind: 'ready', session, activeStep: resumed });
      } catch (err) {
        if (cancellation.cancelled) {
          return;
        }
        const apiErr = err instanceof OnboardingApiError ? err : null;
        setState({
          kind: 'error',
          title: apiErr?.message ?? t('onboarding.ri.errors.bootstrap_failed'),
          detail: apiErr?.detail ?? null,
        });
      }
    })();
    return (): void => {
      cancellation.cancelled = true;
    };
  }, [isAuthenticated, subject, t]);

  const persistStep = useCallback(
    async (stepNumber: OnboardingRiStepNumber, body: OnboardingRiStepBody): Promise<void> => {
      if (state.kind !== 'ready') {
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const updated = await submitOnboardingRiStep(state.session.session_id, stepNumber, body);
        const completed =
          typeof updated.completed_at === 'string' && updated.completed_at.length > 0;
        saveOnboardingRiSnapshot({
          subject,
          sessionId: updated.session_id,
          currentStep: updated.current_step,
          completed,
        });
        if (completed) {
          clearOnboardingRiSnapshot();
          setState({ kind: 'done', session: updated });
          return;
        }
        setState({
          kind: 'ready',
          session: updated,
          activeStep: nextStep(updated.current_step),
        });
      } catch (err) {
        const apiErr = err instanceof OnboardingApiError ? err : null;
        setSubmitError(
          apiErr?.detail ?? apiErr?.message ?? t('onboarding.ri.errors.submit_failed'),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [state, subject, t],
  );

  const handleBack = useCallback((): void => {
    setSubmitError(null);
    setState((prev) => {
      if (prev.kind !== 'ready') {
        return prev;
      }
      if (prev.activeStep <= 1) {
        return prev;
      }
      const target = (prev.activeStep - 1) as OnboardingRiStepNumber;
      return { kind: 'ready', session: prev.session, activeStep: target };
    });
  }, []);

  if (!isAuthenticated) {
    return (
      <section className="urania-onboarding-ri" data-testid="onboarding-ri-unauthenticated">
        <h1 className="display-5 mb-3">{t('onboarding.ri.title')}</h1>
        <div
          className="alert alert-info"
          role="region"
          aria-label={t('onboarding.ri.unauthenticated.aria_label')}
        >
          <p className="mb-2">{t('onboarding.ri.unauthenticated.message')}</p>
          <Link to="/login" className="btn btn-primary" data-testid="onboarding-ri-go-to-login">
            {t('onboarding.ri.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-onboarding-ri" data-testid="onboarding-ri-page">
      <nav className="mb-3">
        <Link to="/dashboard" className="small text-decoration-none">
          ← {t('dashboard.title')}
        </Link>
      </nav>
      <div
        className="rounded mb-3 px-3 px-md-4 py-2 py-md-3 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div className="position-relative" style={{ zIndex: 1, maxWidth: 760 }}>
          <span
            className="badge mb-1"
            style={{
              backgroundColor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontSize: '0.65rem',
              letterSpacing: '0.05em',
            }}
          >
            ONBOARDING · BENEFICIARIO R&amp;I
          </span>
          <h1 className="h5 mb-1">{t('onboarding.ri.title')}</h1>
          <p
            className="mb-0 opacity-90 small"
            style={{ fontSize: '0.78rem' }}
          >
            {t('onboarding.ri.subtitle')}
          </p>
        </div>
      </div>

      <OnboardingStepper
        steps={stepperSteps}
        currentStep={state.kind === 'ready' ? state.activeStep : ONBOARDING_RI_TOTAL_STEPS}
        ariaLabel={t('onboarding.ri.stepper_aria_label')}
        counterLabel={(c, n) => t('onboarding.ri.stepper_counter', { current: c, total: n })}
      />

      <div className="urania-onboarding-ri__form">
        {submitError !== null && (
        <div className="alert alert-danger" role="alert" data-testid="onboarding-ri-submit-error">
          {submitError}
        </div>
      )}

      {state.kind === 'loading' && (
        <p data-testid="onboarding-ri-loading">{t('onboarding.ri.loading')}</p>
      )}

      {state.kind === 'error' && (
        <div
          className="alert alert-danger"
          role="alert"
          data-testid="onboarding-ri-bootstrap-error"
        >
          <strong>{state.title}</strong>
          {state.detail !== null && <p className="mb-0">{state.detail}</p>}
        </div>
      )}

      {state.kind === 'done' && (
        <div className="alert alert-success" role="status" data-testid="onboarding-ri-completed">
          <h2 className="h4 mb-2">{t('onboarding.ri.done.title')}</h2>
          <p className="mb-0">
            {t('onboarding.ri.done.body', {
              outboxEventId: state.session.outbox_event_id ?? 'n/a',
            })}
          </p>
        </div>
      )}

      {state.kind === 'ready' && (
        <div data-testid={`onboarding-ri-step-${String(state.activeStep)}`}>
          {state.activeStep === 1 && (
            <Step1AnagraficaEnte
              defaultValues={{}}
              onSubmitStep={(body) => persistStep(1, body)}
              onBack={null}
              submitting={submitting}
            />
          )}
          {state.activeStep === 2 && (
            <Step2Settore
              defaultValues={{}}
              onSubmitStep={(body) => persistStep(2, body)}
              onBack={handleBack}
              submitting={submitting}
            />
          )}
          {state.activeStep === 3 && (
            <Step3DominioS3
              defaultValues={{}}
              onSubmitStep={(body) => persistStep(3, body)}
              onBack={handleBack}
              submitting={submitting}
            />
          )}
          {state.activeStep === 4 && (
            <Step4TipoEnte
              defaultValues={{}}
              onSubmitStep={(body) => persistStep(4, body)}
              onBack={handleBack}
              submitting={submitting}
            />
          )}
          {state.activeStep === 5 && (
            <Step5TrlProfilo
              defaultValues={{}}
              onSubmitStep={(body) => persistStep(5, body)}
              onBack={handleBack}
              submitting={submitting}
            />
          )}
        </div>
      )}
      </div>
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/onboarding/ri',
  component: OnboardingRiPage,
});
